import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  Briefcase,
  MapPin,
  Clock,
  DollarSign,
  Calendar,
  CheckCircle2,
  Sparkles,
  ArrowRight,
  Send,
  Share2,
  Check,
  Star,
  Layers,
  Award,
  HeartHandshake,
  AlertCircle,
  Loader2,
} from "lucide-react";

import { careersService } from "@/api/careersService";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Reveal } from "@/components/site/SiteLayout";
import { toast } from "@/components/ui/sonner";
import { cn } from "@/lib/utils";

function GlassCard({ className, children }) {
  return (
    <div
      className={cn(
        "glass rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-xl shadow-[0_18px_50px_rgba(0,0,0,0.4)]",
        className
      )}
    >
      {children}
    </div>
  );
}

export default function CareerDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function loadJob() {
      try {
        setLoading(true);
        setError(null);

        const data = await careersService.getJobBySlug(slug);

        if (!isMounted) return;

        if (!data || data.status !== "Published" || data.deleted_at) {
          setError("The requested position was not found or is no longer accepting applications.");
          setJob(null);
        } else {
          setJob(data);

          // Dynamic SEO Metadata
          const pageTitle = data.meta_title || `${data.title} | Careers at Shiveda IT Services`;
          document.title = pageTitle;

          const metaDesc = document.querySelector('meta[name="description"]');
          if (metaDesc) {
            metaDesc.setAttribute(
              "content",
              data.meta_description || data.short_description || `Apply for the ${data.title} role at Shiveda IT Services.`
            );
          }
        }
      } catch (err) {
        console.error("[CareerDetail] Error fetching job:", err);
        if (isMounted) {
          setError("Failed to load position details. Please try again later.");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadJob();

    return () => {
      isMounted = false;
    };
  }, [slug]);

  const handleShare = async () => {
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(window.location.href);
        setCopied(true);
        toast.success("Job link copied to clipboard!");
        setTimeout(() => setCopied(false), 2000);
      }
    } catch {
      toast.info("Link: " + window.location.href);
    }
  };

  const handleApply = () => {
    navigate(`/apply?job=${job.slug}`);
  };

  // Format currency
  const formatSalary = () => {
    if (!job?.salary_visible || !job?.salary_min) return null;
    const currency = job.salary_currency || "INR";
    const minFormatted = Number(job.salary_min).toLocaleString();
    if (job.salary_max) {
      const maxFormatted = Number(job.salary_max).toLocaleString();
      return `${currency} ${minFormatted} - ${maxFormatted}`;
    }
    return `${currency} ${minFormatted}+`;
  };

  // ------------------------------------------------------------
  // Loading State
  // ------------------------------------------------------------
  if (loading) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center space-y-4 px-5">
        <Loader2 className="w-10 h-10 animate-spin text-primary" />
        <p className="text-white/60 text-sm">Loading career opportunity...</p>
      </div>
    );
  }

  // ------------------------------------------------------------
  // Error / Not Found State
  // ------------------------------------------------------------
  if (error || !job) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center px-5 text-center">
        <div className="w-16 h-16 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-400 mb-6">
          <AlertCircle className="w-8 h-8" />
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-3">Position Unavailable</h1>
        <p className="text-white/60 text-sm sm:text-base max-w-md mb-8 leading-relaxed">
          {error || "This role is no longer accepting public applications or does not exist."}
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={() => navigate("/career")}
            className="rounded-xl bg-primary text-black hover:bg-primary/90 btn-glow font-semibold"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Browse Open Positions
          </Button>
          <Button
            onClick={() => navigate("/apply")}
            variant="outline"
            className="rounded-xl border-white/15 bg-white/[0.04] text-white hover:bg-white/[0.08]"
          >
            Submit General Application
          </Button>
        </div>
      </div>
    );
  }

  // Skills segmentation
  const requiredSkills = (job.job_skills || []).filter((s) => s.is_required);
  const preferredSkills = (job.job_skills || []).filter((s) => !s.is_required);

  const deadlineFormatted = job.application_deadline
    ? new Date(job.application_deadline).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  return (
    <div className="space-y-12 pb-20">
      {/* Top Header Banner */}
      <section className="relative pt-8 md:pt-14">
        <div className="w-full px-5 xl:px-10 2xl:px-14">
          <Reveal>
            <div className="mb-6">
              <Link
                to="/career"
                className="inline-flex items-center text-xs font-semibold uppercase tracking-wider text-white/60 hover:text-primary transition-colors"
              >
                <ArrowLeft className="w-3.5 h-3.5 mr-1.5" />
                Back to all positions
              </Link>
            </div>

            <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-8">
              <div className="space-y-4 max-w-3xl">
                <div className="flex flex-wrap items-center gap-2.5">
                  <Badge className="bg-primary/10 text-primary border-primary/25 font-semibold">
                    {job.departments?.name || "Engineering"}
                  </Badge>
                  {job.is_featured && (
                    <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30 flex items-center gap-1 font-semibold">
                      <Star className="w-3 h-3 fill-amber-300" />
                      Featured Role
                    </Badge>
                  )}
                  <Badge variant="outline" className="border-white/15 text-white/70">
                    {job.employment_type}
                  </Badge>
                  <Badge variant="outline" className="border-white/15 text-white/70">
                    {job.workplace_type}
                  </Badge>
                </div>

                <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white leading-tight">
                  {job.title}
                </h1>

                <p className="text-base sm:text-lg text-white/75 leading-relaxed">
                  {job.short_description}
                </p>

                {/* Quick Meta Stats */}
                <div className="pt-2 flex flex-wrap gap-4 sm:gap-6 text-xs sm:text-sm text-white/70">
                  <div className="flex items-center gap-1.5">
                    <MapPin className="w-4 h-4 text-primary" />
                    <span>{job.location || "Remote"}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-4 h-4 text-primary" />
                    <span>
                      {job.experience_min}
                      {job.experience_max ? ` - ${job.experience_max}` : "+"} Years Experience
                    </span>
                  </div>
                  {formatSalary() && (
                    <div className="flex items-center gap-1.5">
                      <DollarSign className="w-4 h-4 text-green-400" />
                      <span className="text-green-400 font-medium">{formatSalary()}</span>
                    </div>
                  )}
                  {deadlineFormatted && (
                    <div className="flex items-center gap-1.5">
                      <Calendar className="w-4 h-4 text-amber-400" />
                      <span>Deadline: {deadlineFormatted}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons Box */}
              <div className="lg:w-72 shrink-0 space-y-3">
                <Button
                  onClick={handleApply}
                  size="lg"
                  className="w-full rounded-xl bg-primary text-black hover:bg-primary/90 btn-glow font-bold text-sm h-12 shadow-[0_0_24px_rgba(0,194,255,0.35)]"
                >
                  <span>Apply For This Role</span>
                  <Send className="w-4 h-4 ml-2" />
                </Button>

                <Button
                  onClick={handleShare}
                  variant="outline"
                  className="w-full rounded-xl border-white/15 bg-white/[0.04] text-white hover:bg-white/[0.08] text-xs h-10"
                >
                  {copied ? (
                    <>
                      <Check className="w-3.5 h-3.5 mr-2 text-green-400" />
                      <span>Link Copied!</span>
                    </>
                  ) : (
                    <>
                      <Share2 className="w-3.5 h-3.5 mr-2" />
                      <span>Share Position</span>
                    </>
                  )}
                </Button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <Separator className="bg-white/10" />

      {/* Main Content & Side Breakdown */}
      <section>
        <div className="w-full px-5 xl:px-10 2xl:px-14">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            {/* Left 2 Columns: Full Job Description */}
            <div className="lg:col-span-2 space-y-10">
              {/* Full Description */}
              {job.full_description && (
                <Reveal delay={100}>
                  <div className="space-y-4">
                    <h2 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-primary" />
                      <span>Role Overview</span>
                    </h2>
                    <div className="text-sm sm:text-base text-white/80 leading-relaxed whitespace-pre-line space-y-4">
                      {job.full_description}
                    </div>
                  </div>
                </Reveal>
              )}

              {/* Responsibilities */}
              {job.responsibilities && job.responsibilities.length > 0 && (
                <Reveal delay={200}>
                  <div className="space-y-4">
                    <h2 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-primary" />
                      <span>Core Responsibilities</span>
                    </h2>
                    <ul className="space-y-3">
                      {job.responsibilities.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-sm sm:text-base text-white/80">
                          <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </Reveal>
              )}

              {/* Requirements */}
              {job.requirements && job.requirements.length > 0 && (
                <Reveal delay={300}>
                  <div className="space-y-4">
                    <h2 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
                      <Award className="w-5 h-5 text-primary" />
                      <span>Required Qualifications & Experience</span>
                    </h2>
                    <ul className="space-y-3">
                      {job.requirements.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-sm sm:text-base text-white/80">
                          <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </Reveal>
              )}

              {/* Preferred Qualifications */}
              {job.preferred_qualifications && job.preferred_qualifications.length > 0 && (
                <Reveal delay={400}>
                  <div className="space-y-4">
                    <h2 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
                      <Star className="w-5 h-5 text-amber-400" />
                      <span>Preferred Qualifications</span>
                    </h2>
                    <ul className="space-y-3">
                      {job.preferred_qualifications.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-sm sm:text-base text-white/80">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-2 shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </Reveal>
              )}

              {/* Benefits */}
              {job.benefits && job.benefits.length > 0 && (
                <Reveal delay={500}>
                  <div className="space-y-4">
                    <h2 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
                      <HeartHandshake className="w-5 h-5 text-green-400" />
                      <span>What We Offer & Benefits</span>
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {job.benefits.map((item, idx) => (
                        <div
                          key={idx}
                          className="p-3.5 rounded-xl border border-white/10 bg-white/[0.02] flex items-center gap-3 text-sm text-white/85"
                        >
                          <div className="w-6 h-6 rounded-lg bg-green-500/15 text-green-400 flex items-center justify-center shrink-0">
                            <Check className="w-3.5 h-3.5" />
                          </div>
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </Reveal>
              )}
            </div>

            {/* Right Column: Sticky Metadata Card */}
            <div className="space-y-6">
              <Reveal delay={200}>
                <GlassCard className="p-6 sm:p-7 space-y-6 sticky top-28">
                  <div>
                    <h3 className="text-base font-bold text-white mb-1">Role Summary</h3>
                    <p className="text-xs text-white/50">Key specifications at a glance</p>
                  </div>

                  <div className="space-y-4 text-xs sm:text-sm">
                    <div className="flex justify-between items-center py-2 border-b border-white/5">
                      <span className="text-white/55">Department</span>
                      <span className="font-semibold text-white">{job.departments?.name || "Engineering"}</span>
                    </div>

                    <div className="flex justify-between items-center py-2 border-b border-white/5">
                      <span className="text-white/55">Employment Type</span>
                      <span className="font-semibold text-white">{job.employment_type}</span>
                    </div>

                    <div className="flex justify-between items-center py-2 border-b border-white/5">
                      <span className="text-white/55">Workplace Mode</span>
                      <span className="font-semibold text-white">{job.workplace_type}</span>
                    </div>

                    <div className="flex justify-between items-center py-2 border-b border-white/5">
                      <span className="text-white/55">Primary Location</span>
                      <span className="font-semibold text-white">{job.location || "Remote"}</span>
                    </div>

                    <div className="flex justify-between items-center py-2 border-b border-white/5">
                      <span className="text-white/55">Experience</span>
                      <span className="font-semibold text-white">
                        {job.experience_min}
                        {job.experience_max ? ` - ${job.experience_max}` : "+"} Years
                      </span>
                    </div>

                    {formatSalary() && (
                      <div className="flex justify-between items-center py-2 border-b border-white/5">
                        <span className="text-white/55">Compensation</span>
                        <span className="font-semibold text-green-400">{formatSalary()}</span>
                      </div>
                    )}
                  </div>

                  {/* Required & Optional Skills */}
                  {job.job_skills && job.job_skills.length > 0 && (
                    <div className="space-y-3 pt-2">
                      <div className="text-xs font-semibold text-white/70 uppercase tracking-wider flex items-center gap-1.5">
                        <Layers className="w-3.5 h-3.5 text-primary" />
                        <span>Technology Stack</span>
                      </div>

                      {requiredSkills.length > 0 && (
                        <div className="space-y-1.5">
                          <div className="text-[11px] text-white/50">Required Proficiencies:</div>
                          <div className="flex flex-wrap gap-1.5">
                            {requiredSkills.map((s, idx) => (
                              <Badge
                                key={idx}
                                className="bg-primary/15 text-primary border-primary/30 text-[11px] py-0.5"
                              >
                                {s.technologies?.name || "Tech"}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {preferredSkills.length > 0 && (
                        <div className="space-y-1.5 pt-1">
                          <div className="text-[11px] text-white/50">Bonus / Preferred:</div>
                          <div className="flex flex-wrap gap-1.5">
                            {preferredSkills.map((s, idx) => (
                              <Badge
                                key={idx}
                                variant="outline"
                                className="border-white/15 text-white/75 text-[11px] py-0.5"
                              >
                                {s.technologies?.name || "Tech"}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="pt-2">
                    <Button
                      onClick={handleApply}
                      className="w-full rounded-xl bg-primary text-black hover:bg-primary/90 btn-glow font-bold text-xs h-11"
                    >
                      <span>Apply Now</span>
                      <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                    </Button>
                  </div>
                </GlassCard>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA Banner */}
      <section className="pt-8">
        <div className="w-full px-5 xl:px-10 2xl:px-14">
          <Reveal delay={200}>
            <GlassCard className="p-8 lg:p-12 text-center relative overflow-hidden">
              <div className="absolute right-0 top-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl pointer-events-none -mr-20 -mt-20" />
              <div className="relative z-10 max-w-2xl mx-auto space-y-4">
                <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white">
                  Ready to Build With Us?
                </h2>
                <p className="text-sm sm:text-base text-white/70 leading-relaxed">
                  Join our engineering culture at Shiveda IT Services. Submit your application in just a few minutes.
                </p>
                <div className="pt-2 flex flex-col sm:flex-row gap-3 justify-center">
                  <Button
                    onClick={handleApply}
                    size="lg"
                    className="rounded-xl bg-primary text-black hover:bg-primary/90 btn-glow font-bold text-sm px-8"
                  >
                    <span>Apply for {job.title}</span>
                    <Send className="w-4 h-4 ml-2" />
                  </Button>
                  <Button
                    onClick={() => navigate("/career")}
                    variant="outline"
                    className="rounded-xl border-white/15 bg-white/[0.03] text-white hover:bg-white/[0.08] text-sm"
                  >
                    View Other Positions
                  </Button>
                </div>
              </div>
            </GlassCard>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
